// src/modules/units/tyres/views/detail/tyredetail.view.js
//
// Pure rendering for the tyre detail view — every function here takes data and
// returns an HTML string, with no data-fetching or DOM side effects. Keeping it
// free of the API layer means it can be rendered and screenshotted in isolation,
// and the orchestrator (tyredetail.js) stays a thin fetch-and-swap shell.

import { hasSensor, isRetired, hasKnownStatus, tyreStatusLabel, tyreStatusTone } from '../../shared/tyre-status.js';
import {
  esc, formatPressure, formatKm, formatTread, formatShortDate, formatMoney, formatDateTime, rotationStatus,
  fitmentSensorId, fitmentUnitName, fitmentPosition, formatWheelPosition
} from '../../shared/tyre-format.js';
import { icon } from '../../shared/tyre-icons.js';
import { tyreLifeStages, currentTyreLifeStage } from '../../shared/tyre-life-stages.js';
import { latestRepairCost, tyreLifetimeCost } from '../../shared/tyre-costs.js';
import {
  TYRE_ACTION,
  resolveActionType, actionLabel, actionTone, actionIcon, getActionDetailRows, positionChange,
  FORM_ACTION_TYPES, actionVerb
} from '../../shared/tyre-action-schema.js';

const MUTED = '<span class="tm-muted">—</span>';
const cell = (v) => (v === null || v === undefined || v === '' || v === '—') ? MUTED : esc(v);
const isBlank = (v) => v === null || v === undefined || v === '';

// Telemetry metric tiles. Temperature/voltage units are safe; pressure and
// rotation units are left blank pending confirmation of the sensor's unit.
const TELEMETRY = [
  { key: 'currentPressure', label: 'Pressure', unit: 'bar', format: formatPressure },
  { key: 'currentTemperature', label: 'Temperature', unit: '°C' },
  { key: 'currentVoltage', label: 'Voltage', unit: 'V' },
  {
    key: 'currentRotation',
    label: 'Rotation',
    render: (v) => {
      const { text, tone } = rotationStatus(v);
      return `<span class="tm-rotation${tone ? ` tm-tone-${tone}` : ''}">${esc(text)}</span>`;
    }
  }
];

function pane(title, inner, extraClass = '') {
  return `<section class="tm-pane ${extraClass}"><h3 class="tm-pane-title">${esc(title)}</h3>${inner}</section>`;
}

function kv(pairs) {
  const rows = pairs.map(([l, v]) => `<div class="tm-kv-row"><dt>${esc(l)}</dt><dd>${v}</dd></div>`).join('');
  return `<dl class="tm-kv">${rows}</dl>`;
}

function money(v) { return isBlank(v) ? '—' : `R ${formatMoney(v)}`; }
function metric(v, unit) { return isBlank(v) ? '—' : `${v}${unit ? ` ${unit}` : ''}`; }
// The tread spec that applies NOW. After a retread the new cap's initial/minimum
// tread and expected mileage live on the current retreadHistory stage; the record
// fields are the fallback (and are what a never-retreaded tyre carries).
function treadSpec(t) {
  const stage = currentTyreLifeStage(t) || {};
  const pick = (a, b) => (isBlank(a) ? b : a);
  return {
    initial: pick(stage.initialTreadDepth, t.initialTyreTreadDepth),
    minimum: pick(stage.minimumTread, t.minimumTreadForRetread),
    mileage: pick(stage.expectedMileage, t.manufacturerExpectedMileage)
  };
}

function kmPerMmText(v) {
  const n = Number(v);
  return isBlank(v) || !Number.isFinite(n) ? '—' : `${Math.round(n).toLocaleString('en-ZA')} km/mm`;
}

// Signed difference badge, oriented so "better than expected" is always positive
// and green: more km per mm, more mileage, more tread left.
function deltaBadge(actual, expected, kind) {
  const a = Number(actual);
  const e = Number(expected);
  if (isBlank(actual) || isBlank(expected) || !Number.isFinite(a) || !Number.isFinite(e)) return '';
  let d;
  let text;
  if (kind === 'pct') {
    if (e === 0) return '';
    d = Math.round(((a - e) / e) * 100);
    text = `${d > 0 ? '+' : d < 0 ? '−' : ''}${Math.abs(d)}%`;
  } else {
    d = Math.round((a - e) * 100) / 100;
    text = `${d > 0 ? '+' : d < 0 ? '−' : ''}${formatTread(Math.abs(d))}`;
  }
  const tone = d > 0 ? 'green2' : d < 0 ? 'red' : 'slate';
  return `<span class="tm-cmp-delta tm-tone-${tone}">${esc(text)}</span>`;
}

// Expected km per 1 mm of usable tread for the CURRENT stage: the backend's value
// when sent, else mileage ÷ (initial − minimum). Null when it can't be worked out.
function expectedKmPerMmValue(t) {
  if (!isBlank(t.expectedKmPerMm) && Number.isFinite(Number(t.expectedKmPerMm))) return Number(t.expectedKmPerMm);
  const spec = treadSpec(t);
  const initial = Number(spec.initial);
  const minimum = Number(spec.minimum);
  const mileage = Number(spec.mileage);

  if (
    isBlank(spec.initial) || isBlank(spec.minimum) || isBlank(spec.mileage) ||
    !Number.isFinite(initial) ||
    !Number.isFinite(minimum) ||
    !Number.isFinite(mileage) ||
    initial <= minimum ||
    mileage <= 0
  ) {
    return null;
  }

  return mileage / (initial - minimum);
}

// ---- header --------------------------------------------------------------
export function headHTML(tyre = {}) {
  // Derived, not stored: a tyre on a sensor with no vehicle reads "Fitted" (blue),
  // and "In Service" (green) once it is on one. See tyre-status.js.
  const badge = hasKnownStatus(tyre)
    ? `<span class="tm-badge tm-tone-${tyreStatusTone(tyre)}">${esc(tyreStatusLabel(tyre))}</span>`
    : MUTED;
  return `
    <div class="tm-detail-id">
      <span class="tm-detail-id-label">Tyre</span>
      <span class="tm-detail-id-value">${cell(tyre.externalTyreId)}</span>
    </div>
    ${badge}`;
}

// Action buttons for the detail header. The details-capture actions are always
// available; the association action depends on whether the tyre is fitted —
// Fit when it's in stock, Move + Remove when it's on a sensor. Wired by the
// orchestrator via [data-act].
const ASSOC_META = {
  edit: { verb: 'Edit', icon: 'edit', tone: 'slate' },
  // Fit is a primary action, so it wears the primary blue rather than the green
  // "fitted" tone — and it matches the Fit badge on the association form itself.
  fit: { verb: 'Fit', icon: 'fit', tone: 'primary-blue' },
  // Move (fit straight to another sensor) is intentionally NOT offered here: a tyre
  // goes back to stock first (Move to Stock, below) and is then Fitted to the new
  // sensor, so every association starts from stock. The mode is still supported end
  // to end (association form + router), so re-adding 'move' to the fitted keys in
  // actionsBarHTML restores the button.
  move: { verb: 'Move', icon: 'rotate', tone: 'amber' },
  remove: { verb: 'Move to Stock', icon: 'fit', tone: 'slate' }
};

export function actionsBarHTML(tyre = {}) {
  const fitted = hasSensor(tyre);
  // A tyre that has already left the fleet cannot leave it again. Everything else
  // stays available on purpose: a "missing" tyre that turns up is refitted, not
  // resurrected through some undo action the backend enum does not have.
  const retired = isRetired(tyre);
  const keys = ['edit', ...(fitted ? ['remove'] : ['fit']), ...FORM_ACTION_TYPES]
    .filter((k) => !(retired && k === TYRE_ACTION.RETIRE));
  return keys.map((k) => {
    const verb = ASSOC_META[k]?.verb || actionVerb(k);
    const ic = ASSOC_META[k]?.icon || actionIcon(k);
    const tone = ASSOC_META[k]?.tone || actionTone(k);
    return `<button type="button" class="tm-detail-act tm-tone-${tone}" data-act="${k}">
      <span class="tm-detail-act-ic">${icon(ic)}</span>${esc(verb)}
    </button>`;
  }).join('');
}

// ---- panes ---------------------------------------------------------------

// Current snapshot only. The immutable original/retread specifications are shown
// together in the full-width Tyre life stages section below these panes.
function specPane(t) {
  const identity = [
    ['Manufacturer / brand', cell(t.tyreBrand || t.tyreMake)],
    ['Model', cell(t.tyreModel)],
    ['Tread pattern', cell(t.tyreTreadPattern)],
    ['Size', cell(t.tyreSize)],
    ['Type', cell(t.tyreType)],
    ['Application', cell(t.tyreApplication)],
    ['Grade', cell(t.tyreGrade)],
    ['Serial number', cell(t.tyreSerialNumber)],
    ['RFID', cell(t.tyreRFID)]
  ];
  const purchase = [
    ['Supplier', cell(t.supplierName)],
    ['Invoice No', cell(t.invoiceNo)],
    ['Invoice date', cell(formatShortDate(t.purchaseDate))],
    ['Purchase cost (excl VAT)', cell(money(t.purchaseCost))]
  ];
  const inner = kv([...identity, ...purchase]);

  const notes = t.notes
    ? `<div class="tm-pane-notes">
        <span class="tm-kv-note-label">Notes</span>
        <p>${esc(t.notes)}</p>
      </div>`
    : '';

  return pane('Specification', inner + notes);
}

// `actions` is the loaded action history (may be empty while it loads); it feeds
// the latest repair cost until the backend returns a repairHistory on the record.
function conditionPane(t, actions) {
  const spec = treadSpec(t);
  const cur = isBlank(t.tyreTreadDepth) ? NaN : Number(t.tyreTreadDepth);
  const init = isBlank(spec.initial) ? NaN : Number(spec.initial);
  const min = isBlank(spec.minimum) ? NaN : Number(spec.minimum);

  let wear = '';

  if (
    Number.isFinite(cur) &&
    Number.isFinite(init) &&
    Number.isFinite(min) &&
    init > min
  ) {
    const usableTread = init - min;
    const usableRemaining = Math.max(0, cur - min);

    const pct = Math.max(
      0,
      Math.min(100, (usableRemaining / usableTread) * 100)
    );

    const tone =
      pct < 20 ? 'red' :
        pct < 40 ? 'amber' :
          'green';

    wear = `
      <div class="tm-wear">
        <div class="tm-wear-head">
          <span>Usable tread remaining</span>
          <b>${Math.round(pct)}%</b>
        </div>

        <div class="tm-wear-track">
          <span
            class="tm-wear-fill tm-tone-${tone}"
            style="width:${pct.toFixed(1)}%"
          ></span>
        </div>

        <div class="tm-wear-sub">
          <span>${esc(formatTread(cur))} now${sourceTag(t)}</span>
          <span class="tm-muted">
            ${esc(formatTread(min))} retread limit
          </span>
        </div>
      </div>`;
  }

  const rows = kv([
    ['Initial tread', cell(formatTread(spec.initial))],
    ['Minimum tread', cell(formatTread(spec.minimum))],
    ['Current tread', isBlank(t.tyreTreadDepth) ? MUTED : `${esc(formatTread(t.tyreTreadDepth))}${sourceTag(t)}`],
    ['Tread updated', cell(formatDateTime(t.tyreTreadDepthUpdatedAt))],
    ['Last measured', lastMeasuredCell(t)],
    ['Km since measured', cell(kmSinceMeasured(t))]
  ]);

  const tail = kv([
    ['Wear pattern', cell(t.tyreWearPattern)],
    ['Damage', cell(t.tyreDamageSeverity)],
    ['Repair type', cell(t.tyreRepairType)],
    ['Latest repair cost', latestRepairCell(t, actions)]
  ]);

  return pane('Condition & tyre life', wear + rows + compareBlock(t, spec) + tail);
}

// "Measured" = the value an inspection logged; "Estimated" = the backend has worn
// it down from there by the km driven since.
function sourceTag(t) {
  const src = String(t.treadDepthSource || '').toUpperCase();
  if (src === 'MEASURED') return ' <span class="tm-src-tag tm-tone-green2">Measured</span>';
  if (src === 'ESTIMATED') return ' <span class="tm-src-tag tm-tone-blue">Estimated</span>';
  return '';
}

function lastMeasuredCell(t) {
  if (isBlank(t.lastMeasuredTreadDepth)) return MUTED;
  const when = formatShortDate(t.lastMeasuredAt);
  return `${esc(formatTread(t.lastMeasuredTreadDepth))}${when && when !== '—' ? ` <span class="tm-muted">· ${esc(when)}</span>` : ''}`;
}

function kmSinceMeasured(t) {
  if (isBlank(t.tyreKm) || isBlank(t.tyreKmAtLastMeasurement)) return '—';
  const d = Number(t.tyreKm) - Number(t.tyreKmAtLastMeasurement);
  return Number.isFinite(d) ? formatKm(Math.max(0, d)) : '—';
}

// A retread date is projected from the recent km-per-day rate; off a near-idle
// window that lands centuries out (a real case gave 2209). Anything past this
// horizon says nothing useful, so it shows as unknown rather than as a date.
const RETREAD_DATE_HORIZON_YEARS = 5;
function plausibleRetreadDate(v) {
  const t = new Date(v).getTime();
  if (isBlank(v) || Number.isNaN(t)) return '—';
  const horizon = new Date();
  horizon.setFullYear(horizon.getFullYear() + RETREAD_DATE_HORIZON_YEARS);
  return t > horizon.getTime() ? '—' : formatShortDate(v);
}

// Manufacturer expectation beside what this tyre is really doing.
function compareBlock(t, spec) {
  const row = (label, expected, actual, delta) =>
    `<tr><th scope="row">${esc(label)}</th><td>${cell(expected)}</td><td>${cell(actual)}${delta}</td></tr>`;
  const body = [
    row('Km per mm', kmPerMmText(expectedKmPerMmValue(t)), kmPerMmText(t.actualKmPerMm),
      deltaBadge(t.actualKmPerMm, expectedKmPerMmValue(t), 'pct')),
    row('Stage mileage', isBlank(spec.mileage) ? '—' : formatKm(spec.mileage),
      isBlank(t.projectedMileage) ? '—' : formatKm(t.projectedMileage), deltaBadge(t.projectedMileage, spec.mileage, 'pct')),
    row('Tread now', formatTread(t.expectedTreadDepth), formatTread(t.tyreTreadDepth),
      deltaBadge(t.tyreTreadDepth, t.expectedTreadDepth, 'mm')),
    row('Retread due', '—', plausibleRetreadDate(t.estimatedRetreadDate), '')
  ].join('');
  return `
    <div class="tm-cmp">
      <div class="tm-kmbar-label">Expected vs actual</div>
      <table class="tm-cmp-table">
        <thead><tr><th></th><th scope="col">Expected</th><th scope="col">Actual</th></tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>`;
}

function latestRepairCell(t, actions) {
  const r = latestRepairCost(t, actions);
  if (!r) return MUTED;
  const when = formatShortDate(r.date);
  return `${esc(money(r.cost))}${when && when !== '—' ? ` <span class="tm-muted">· ${esc(when)}</span>` : ''}`;
}

function telemetryPane(t) {
  const fitted = hasSensor(t);
  if (!fitted) {
    return pane('Live telemetry', `
      <div class="tm-empty-pane">
        <span class="tm-empty-icon">${icon('tyre')}</span>
        <p>Not fitted — no live sensor data.</p>
        <p class="tm-muted">Telemetry appears once this tyre is fitted to a wheel sensor.</p>
      </div>`);
  }
  const tiles = TELEMETRY.map((m) => {
    const v = t[m.key];
    let val;
    if (m.render) {
      val = m.render(v);                                   // metric supplies its own markup
    } else if (isBlank(v)) {
      val = '—';
    } else {
      const shown = esc(m.format ? m.format(v) : v);        // number only; unit is the span below
      val = `${shown}${m.unit ? `<span class="tm-metric-unit">${esc(m.unit)}</span>` : ''}`;
    }
    return `<div class="tm-metric"><span class="tm-metric-value">${val}</span><span class="tm-metric-label">${esc(m.label)}</span></div>`;
  }).join('');
  const rows = kv([
    ['Sensor', cell(fitmentSensorId(t))],
    ['Unit', cell(fitmentUnitName(t))],
    ['Position', cell(fitmentPosition(t))],
    ['Highest temp', cell(metric(t.highestTemperature, '°C'))],
    ['Last payload', cell(formatDateTime(t.payloadTS))]
  ]);
  return pane('Live telemetry', `<div class="tm-metrics">${tiles}</div>${rows}`);
}

function kmSegments(label, segments) {
  const total = segments.reduce((s, x) => s + (Number(x.value) || 0), 0);
  if (!total) return '';
  const track = segments.map((s) => {
    const v = Number(s.value) || 0;
    const pct = (v / total) * 100;
    return pct > 0 ? `<span class="tm-kmseg tm-tone-${s.tone}" style="width:${pct.toFixed(2)}%"></span>` : '';
  }).join('');
  const legend = segments.map((s) => {
    const v = Number(s.value) || 0;
    const pct = Math.round((v / total) * 100);
    return `<span class="tm-kmleg"><span class="tm-kmdot tm-tone-${s.tone}"></span>${esc(s.label)} <b>${esc(formatKm(v))}</b> <span class="tm-muted">(${pct}%)</span></span>`;
  }).join('');
  return `<div class="tm-kmbar"><div class="tm-kmbar-label">${esc(label)}</div><div class="tm-kmtrack">${track}</div><div class="tm-kmlegend">${legend}</div></div>`;
}

// Lifetime cost of ownership: purchase + retreads + repairs, over lifetime km.
function costBlock(t, actions) {
  const c = tyreLifetimeCost(t, actions);
  const perKm = c.costPerKm === null ? '—' : `R ${formatMoney(c.costPerKm)}/km`;
  const count = (n, one, many) => (n ? ` <span class="tm-muted">(${n} ${n === 1 ? one : many})</span>` : '');
  const note = c.repairSource === 'actions' && c.repairCount
    ? `<p class="tm-muted tm-cost-note">Repairs are totalled from the loaded action history.</p>`
    : '';
  return `
    <div class="tm-cost">
    <div class="tm-kmbar-label">Cost of ownership</div>
    <div class="tm-metrics">
      <div class="tm-metric">
        <span class="tm-metric-value">${esc(c.total === null ? '—' : money(c.total))}</span>
        <span class="tm-metric-label">Lifetime cost (excl VAT)</span>
      </div>
      <div class="tm-metric">
        <span class="tm-metric-value">${esc(perKm)}</span>
        <span class="tm-metric-label">Cost per km</span>
      </div>
    </div>
    ${kv([
      ['Purchase', cell(money(c.purchase))],
      ['Retreads', `${cell(money(c.retreads))}${count(c.retreadCount, 'retread', 'retreads')}`],
      ['Repairs', `${cell(money(c.repairs))}${count(c.repairCount, 'repair', 'repairs')}`]
    ])}
    ${note}
    </div>`;
}

function remainingSource(t) {
  const src = String(t.estimatedKmRemainingSource || '').toUpperCase();
  if (src === 'INSUFFICIENT_DATA') return '<span class="tm-metric-sub">not enough data yet</span>';
  if (!src || isBlank(t.estimatedKmRemaining)) return '';
  const text = src === 'ACTUAL_WEAR_RATE' ? 'actual wear' : 'manufacturer';
  return `<span class="tm-metric-sub">based on ${esc(text)}</span>`;
}

function kmPane(t, actions) {
  const byPressure = kmSegments('By pressure', [
    {
      label: 'Over-inflated',
      value: t.tyreKmOverinflated,
      tone: 'presshigh'
    },
    {
      label: 'Normal',
      value: t.tyreKmNormal,
      tone: 'normal'
    },
    {
      label: 'Under-inflated',
      value: t.tyreKmUnderinflated,
      tone: 'presslow'
    }
  ]);

  const byTemp = kmSegments('By temperature', [
    {
      label: 'High',
      value: t.tyreKmTemperatureHigh,
      tone: 'temphigh'
    },
    {
      label: 'Normal',
      value: t.tyreKmTemperatureNormal,
      tone: 'normal'
    }
  ]);

  const totalDistance = isBlank(t.tyreKm)
    ? '—'
    : esc(formatKm(t.tyreKm));

  const estimatedRemaining = isBlank(t.estimatedKmRemaining)
    ? '—'
    : esc(formatKm(t.estimatedKmRemaining));

  const summary = `
    <div class="tm-metrics">

      <div class="tm-metric">
        <span class="tm-metric-value">
          ${totalDistance}
        </span>
        <span class="tm-metric-label">
          Total distance
        </span>
      </div>

      <div class="tm-metric">
        <span class="tm-metric-value">
          ${estimatedRemaining}
        </span>
        <span class="tm-metric-label">
          Estimated km remaining${remainingSource(t)}
        </span>
      </div>

    </div>`;

  const stage = currentTyreLifeStage(t);
  const distanceUpdated = kv([
    ...(Number(stage?.stageNo) > 0 && !isBlank(stage?.tyreKm)
      ? [['Distance this stage', cell(formatKm(stage.tyreKm))]]
      : []),
    ['Distance last updated', cell(formatDateTime(t.tyreKmUpdatedAt))]
  ]);

  const breakdown = (byPressure || byTemp)
    ? byPressure + byTemp
    : `
      <div class="tm-empty-pane">
        <p class="tm-muted">
          No pressure or temperature distance breakdown recorded yet.
        </p>
      </div>`;

  return pane(
    'Distance & tyre life',
    summary + distanceUpdated + breakdown + costBlock(t, actions)
  );
}

/** Inspection measurements, newest first, each against the tread expected then. */
export function treadHistoryHTML(tyre = {}) {
  const list = Array.isArray(tyre.treadHistory) ? tyre.treadHistory.filter(Boolean) : [];
  if (!list.length) return '';
  const time = (v) => { const n = new Date(v).getTime(); return Number.isNaN(n) ? 0 : n; };
  const rows = [...list]
    .sort((a, b) => time(b.date) - time(a.date))
    .map((h) => `<tr>
        <td>${cell(formatShortDate(h.date))}</td>
        <td>${cell(isBlank(h.tyreKm) ? '—' : formatKm(h.tyreKm))}</td>
        <td>${cell(formatTread(h.measuredTread))}</td>
        <td>${cell(formatTread(h.expectedTread))}</td>
        <td>${deltaBadge(h.measuredTread, h.expectedTread, 'mm') || MUTED}</td>
      </tr>`).join('');
  return `<section class="tm-pane tm-pane-full">
    <h3 class="tm-pane-title">Tread measurements</h3>
    <div class="tm-cmp-scroll"><table class="tm-cmp-table tm-tread-hist">
      <thead><tr><th scope="col">Date</th><th scope="col">Distance</th><th scope="col">Measured</th><th scope="col">Expected</th><th scope="col">Difference</th></tr></thead>
      <tbody>${rows}</tbody>
    </table></div>
  </section>`;
}

export function panesHTML(tyre = {}, actions = []) {
  return specPane(tyre) + conditionPane(tyre, actions) + telemetryPane(tyre) + kmPane(tyre, actions);
}

function numberWithUnit(value, formatter, unit = '') {
  if (isBlank(value)) return MUTED;
  return cell(`${formatter(value)}${unit}`);
}

function stageTitle(stage = {}) {
  if (!isBlank(stage.grade)) return String(stage.grade);
  const no = Number(stage.stageNo);
  if (!Number.isFinite(no) || no <= 0) return 'New';
  if (no === 1) return 'Retread First Cap';
  if (no === 2) return 'Retread Second Cap';
  if (no === 3) return 'Retread Third Cap';
  return `Retread Stage ${no}`;
}

function stageTypeLabel(stage = {}) {
  return String(stage.type || '').toUpperCase() === 'ORIGINAL' ? 'Original' : 'Retread';
}

function stagePerformanceValue(value, kind) {
  if (isBlank(value)) return MUTED;
  if (kind === 'km') return cell(formatKm(value));
  if (kind === 'tread') return cell(formatTread(value));
  if (kind === 'wearRate') return cell(`${Number(value).toLocaleString('en-ZA', { maximumFractionDigits: 6 })} mm/km`);
  if (kind === 'kmPerMm') return cell(`${Math.round(Number(value)).toLocaleString('en-ZA')} km/mm`);
  if (kind === 'costPerKm') return cell(`R ${formatMoney(value)}/km`);
  return cell(value);
}

function stageCardHTML(stage, current) {
  const stageNo = Number(stage.stageNo);
  const spec = kv([
    ['Product', cell(stage.productName)],
    ['Tread pattern', cell(stage.treadPattern)],
    ['Started', cell(formatShortDate(stage.startDate))],
    ['Initial tread', numberWithUnit(stage.initialTreadDepth, formatTread)],
    ['Minimum tread', numberWithUnit(stage.minimumTread, formatTread)],
    ['Expected mileage', isBlank(stage.expectedMileage) ? MUTED : cell(formatKm(stage.expectedMileage))],
    ['Cost (excl VAT)', isBlank(stage.cost) ? MUTED : cell(money(stage.cost))]
  ]);
  const performance = kv([
    ['Distance run', stagePerformanceValue(stage.tyreKm, 'km')],
    ['Tread worn', stagePerformanceValue(stage.treadWorn, 'tread')],
    ['Avg wear rate', stagePerformanceValue(stage.avgWearRate, 'wearRate')],
    ['Km per 1 mm', stagePerformanceValue(stage.kmPerMm, 'kmPerMm')],
    ['Cost per km', stagePerformanceValue(stage.costPerKm, 'costPerKm')]
  ]);

  return `<article class="tm-life-stage${current ? ' is-current' : ''}">
    <div class="tm-life-stage-head">
      <div><h4>${esc(stageTitle(stage))}</h4><span>Stage ${Number.isFinite(stageNo) ? stageNo : '—'} · ${esc(stageTypeLabel(stage))}</span></div>
      <div class="tm-life-stage-badges"><span class="tm-life-stage-type">${esc(stageTypeLabel(stage))}</span>${current ? '<span class="tm-life-stage-current">Current stage</span>' : ''}</div>
    </div>
    <div class="tm-life-stage-body">
      <section><h5>Specification</h5>${spec}</section>
      <section><h5>Performance</h5>${performance}</section>
    </div>
  </article>`;
}

/** Full casing history. Null performance values stay unknown; they never become 0. */
export function lifeStagesHTML(tyre = {}) {
  const stages = tyreLifeStages(tyre);
  if (!stages.length) return '';
  const current = currentTyreLifeStage(tyre);
  const lifetime = isBlank(tyre.tyreKm) ? '—' : formatKm(tyre.tyreKm);
  return `<section class="tm-pane tm-pane-full tm-life-stages">
    <div class="tm-life-stages-head">
      <div><h3 class="tm-pane-title">Tyre life stages</h3><p>Original casing plus each completed retread stage recorded for this tyre.</p></div>
      <div class="tm-life-stage-summary">
        <span>Current grade: <b>${cell(tyre.tyreGrade)}</b></span>
        <span>Lifetime distance: <b>${cell(lifetime)}</b></span>
        <span>Recorded stages: <b>${stages.length}</b></span>
      </div>
    </div>
    <div class="tm-life-stage-grid">${stages.map((stage) => stageCardHTML(stage, stage === current)).join('')}</div>
  </section>`;
}

// ---- action history timeline --------------------------------------------
function skeletonTimeline() {
  return Array.from({ length: 3 }, () => `<div class="tm-tl-skel"></div>`).join('');
}

function timelineItem(a) {
  const type = resolveActionType(a);
  // Date, not date-time. These lifecycle events carry a DATE, not a wall-clock
  // moment: the capture forms collect a day, and a back-dated action (every
  // retread send/return included) is stamped at LOCAL NOON so it can't slip a day
  // under UTC. Rendering that noon as "12:00 PM" dressed a placeholder up as real
  // data — so the timeline shows the day and lets the ordering below carry the
  // within-day sequence. formatShortDate keeps the timeline on the one date format
  // the rest of the detail page uses (and the pending SA-format change reaches it
  // for free).
  const when = formatShortDate(a.timestamp || a.createdAt);
  const pos = positionChange(a, formatWheelPosition);
  const posChip = pos
    ? `<span class="tm-tl-move">${esc(pos.from)} <span class="tm-tl-arrow">→</span> ${esc(pos.to)}</span>`
    : '';
  const rows = getActionDetailRows(a);
  const details = rows.length
    ? `<div class="tm-tl-details">${rows.map((r) => `<span class="tm-tl-detail"><span class="tm-tl-detail-label">${esc(r.label)}:</span> ${esc(r.value)}</span>`).join('')}</div>`
    : '';
  const who = a.createdBy ? `<div class="tm-tl-meta">by <span class="tm-tl-who">${esc(a.createdBy)}</span></div>` : '';
  return `
    <li class="tm-tl-item">
      <span class="tm-tl-dot tm-tone-${actionTone(type)}">${icon(actionIcon(type))}</span>
      <div class="tm-tl-body">
        <div class="tm-tl-head">
          <span class="tm-tl-label">${esc(actionLabel(type))}</span>
          ${posChip}
          <span class="tm-tl-when">${esc(when)}</span>
        </div>
        ${who}
        ${details}
      </div>
    </li>`;
}

// Milliseconds for an ISO value, or null when it is missing/unparseable (so `??`
// can fall through to the next field rather than treating a bad date as epoch 0).
const instant = (v) => { const t = new Date(v).getTime(); return Number.isNaN(t) ? null : t; };

// Reverse-chronological ordering for the history — newest first. The primary key is
// the event's own moment (`timestamp`, the day the action happened). Retread
// send/return, and any back-dated action, are stamped at LOCAL NOON (a date has no
// time; noon dodges a UTC day-slip), so two events on the SAME day tie on `timestamp`
// alone — which is what let "Sent for retread" sit above the "Retreaded" that answers
// it, the send having been returned first by the API. The tie falls to the record's
// real creation instant (`createdAt`), then its id — both of which rise with write
// order — so the return (recorded after its send) sorts above the send. Exported pure
// so the ordering can be checked in a Node harness, no DOM.
export function compareActionsDesc(a = {}, b = {}) {
  const ea = instant(a.timestamp) ?? instant(a.createdAt) ?? 0;
  const eb = instant(b.timestamp) ?? instant(b.createdAt) ?? 0;
  if (ea !== eb) return eb - ea;                      // newest event day first

  const ca = instant(a.createdAt) ?? instant(a.timestamp) ?? 0;
  const cb = instant(b.createdAt) ?? instant(b.timestamp) ?? 0;
  if (ca !== cb) return cb - ca;                       // same day: last recorded first

  const na = Number(a.id);
  const nb = Number(b.id);                             // final tiebreak: id, newest first
  if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return nb - na;
  const sa = a.id == null ? '' : String(a.id);
  const sb = b.id == null ? '' : String(b.id);
  return sa < sb ? 1 : sa > sb ? -1 : 0;
}

export function timelineHTML(state = {}) {
  if (state.loading) return `<div class="tm-tl-loading">${skeletonTimeline()}</div>`;
  if (state.error) return `<div class="tm-tl-state tm-tl-error">${esc(state.error)}</div>`;
  const actions = Array.isArray(state.actions) ? state.actions : [];
  if (!actions.length) return `<div class="tm-tl-state tm-muted">No actions recorded for this tyre yet.</div>`;
  const sorted = [...actions].sort(compareActionsDesc);
  return `<ol class="tm-timeline">${sorted.map(timelineItem).join('')}</ol>`;
}

// Inline confirm for logging a rotation. Rendered once (hidden) beneath the action
// bar and toggled by the orchestrator, rather than pushing a whole screen: a
// rotation carries no data beyond its date, so a one-line confirm keeps the user
// on the tyre they're inspecting. The date defaults to today (set by the
// orchestrator, which also caps it at today); backdating covers a rotation logged
// after the fact.
export function rotatePanelHTML() {
  return `
    <div class="tm-rotate-panel" data-rotate-panel hidden>
      <div class="tm-rotate-head">
        <span class="tm-rotate-ic tm-tone-amber">${icon('rotate')}</span>
        <div>
          <h3 class="tm-rotate-title">Log a rotation</h3>
          <p class="tm-rotate-sub">Records that this tyre changed wheel position. The sensor stays paired — the system tracks the new position and on-truck distance, so there's nothing else to enter.</p>
        </div>
      </div>
      <div class="tm-form-msg" data-rotate-msg hidden></div>
      <div class="tm-rotate-body">
        <div class="tm-rotate-field">
          <label class="tm-form-label" for="tm-rotate-date">Rotation date</label>
          <input type="date" id="tm-rotate-date" class="tm-input" data-rotate-date />
        </div>
        <div class="tm-rotate-actions">
          <button type="button" class="tm-btn-secondary" data-rotate-cancel>Cancel</button>
          <button type="button" class="tm-btn-primary" data-rotate-save>Log rotation</button>
        </div>
      </div>
    </div>`;
}
